import dagre from 'dagre';
import { MarkerType } from 'reactflow';
import { getWikiLink as getSharedWikiLink, isQuestUnlocked as sharedIsQuestUnlocked } from '../../utils/constants';

export const COLLECTOR_QUEST_ID = "5c51aac186f77432ea65c552";

export const TRADER_CONFIG = {
  'Prapor': { index: 0, color: '#eab308', img: 'https://assets.tarkov.dev/trader-prapor.jpg' },
  'Therapist': { index: 1, color: '#10b981', img: 'https://assets.tarkov.dev/trader-therapist.jpg' },
  'Skier': { index: 2, color: '#3b82f6', img: 'https://assets.tarkov.dev/trader-skier.jpg' },
  'Peacekeeper': { index: 3, color: '#06b6d4', img: 'https://assets.tarkov.dev/trader-peacekeeper.jpg' },
  'Mechanic': { index: 4, color: '#a855f7', img: 'https://assets.tarkov.dev/trader-mechanic.jpg' },
  'Ragman': { index: 5, color: '#f97316', img: 'https://assets.tarkov.dev/trader-ragman.jpg' },
  'Jaeger': { index: 6, color: '#22c55e', img: 'https://assets.tarkov.dev/trader-jaeger.jpg' },
  'Fence': { index: 7, color: '#71717a', img: 'https://assets.tarkov.dev/trader-fence.jpg' },
};

export const NODE_WIDTH = 1600;
export const NODE_HEIGHT = 700;
export const COMPACT_WIDTH = 1000;
export const COMPACT_HEIGHT = 450;
export const ROW_HEIGHT = 1200;
export const TRADER_COL_WIDTH = 1200;
export const OFFSET_X = 1600;

export const isQuestUnlocked = sharedIsQuestUnlocked;
export const getWikiLink = getSharedWikiLink;

export const calculateLayout = (quests, completedQuests, language, isQuestUnlockedFunc, playerLevel) => {
  const questNodes = [];
  const edges = [];
  const laneNodes = [];
  const questMap = new Map(quests.map(q => [q.id, q]));

  const TRADER_ORDER_NORMALIZED = ['prapor', 'therapist', 'skier', 'peacekeeper', 'mechanic', 'ragman', 'jaeger', 'fence'];

  let currentY = 0;
  let GLOBAL_MAX_X = 0;

  TRADER_ORDER_NORMALIZED.forEach(normalizedName => {
    const traderQuests = quests.filter(q => q.trader?.normalizedName === normalizedName);
    if (traderQuests.length === 0) return;

    const traderDisplayName = traderQuests[0].trader?.name || normalizedName;

    const g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: 'LR', ranksep: 1400, nodesep: 400 }); // Daha sıkı bir düzen
    g.setDefaultEdgeLabel(() => ({}));

    traderQuests.forEach(quest => {
      const isCompact = quest.name.includes(' - Part') || quest.name.includes('Gunsmith') || quest.name.includes('Tarkov Shooter') || quest.name.includes('Часть');
      g.setNode(quest.id, {
        width: isCompact ? COMPACT_WIDTH : NODE_WIDTH,
        height: isCompact ? COMPACT_HEIGHT : NODE_HEIGHT,
        isCompact
      });

      if (quest.id !== COLLECTOR_QUEST_ID) {
        quest.taskRequirements?.forEach(req => {
          const parent = questMap.get(req.task.id);
          if (parent && parent.trader?.normalizedName === normalizedName && parent.id !== COLLECTOR_QUEST_ID) {
            g.setEdge(req.task.id, quest.id);
          }
        });
      }
    });

    dagre.layout(g);

    let maxLaneHeight = 0;
    g.nodes().forEach(v => {
      const n = g.node(v);
      if (n.y + n.height > maxLaneHeight) maxLaneHeight = n.y + n.height;
      if (n.x + n.width + OFFSET_X > GLOBAL_MAX_X) GLOBAL_MAX_X = n.x + n.width + OFFSET_X;
    });

    const laneHeight = Math.max(ROW_HEIGHT, maxLaneHeight + 400);

    g.nodes().forEach(nodeId => {
      const n = g.node(nodeId);
      const quest = questMap.get(nodeId);
      const isCompleted = completedQuests.includes(quest.id);
      const isActive = !isCompleted && isQuestUnlockedFunc(quest, completedQuests, playerLevel);
      const isLocked = !isCompleted && !isActive;

      const externalReqs = [];
      if (quest.id !== COLLECTOR_QUEST_ID) {
        quest.taskRequirements?.forEach(req => {
          const parent = questMap.get(req.task.id);
          if (parent && parent.trader?.normalizedName !== normalizedName) {
            externalReqs.push({ trader: parent.trader?.normalizedName || parent.trader?.name?.toLowerCase(), quest: parent.name });
          }
        });
      }

      questNodes.push({
        id: quest.id,
        type: 'quest',
        position: { x: n.x + OFFSET_X - (n.width / 2), y: currentY + n.y + 200 - (n.height / 2) },
        dragHandle: '.custom-drag-handle',
        data: {
          label: quest.name,
          trader: traderDisplayName,
          level: quest.minPlayerLevel,
          completed: isCompleted,
          locked: isLocked,
          isActive: isActive,
          isCompact: n.isCompact,
          externalReqs: [...new Set(externalReqs)],
          wikiLink: getWikiLink(quest, language),
          onToggle: null
        },
        draggable: true,
        zIndex: isActive ? 20 : 10,
      });
    });

    g.edges().forEach(edge => {
      const isCompleted = completedQuests.includes(edge.w);
      edges.push({
        id: `${edge.v}-${edge.w}`,
        source: edge.v,
        target: edge.w,
        type: 'default',
        animated: false,
        style: {
          stroke: isCompleted ? '#10b981' : '#3f3f46',
          strokeWidth: 20,
          opacity: isCompleted ? 0.7 : 0.3
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isCompleted ? '#10b981' : '#3f3f46',
          width: 20,
          height: 20
        },
      });
    });

    laneNodes.push({
      id: `lane-${normalizedName}`,
      type: 'lane',
      position: { x: 0, y: currentY },
      data: {
        trader: traderDisplayName,
        height: laneHeight,
        width: 0,
        imageLink: traderQuests[0]?.trader?.imageLink || TRADER_CONFIG['Fence'].img
      },
      draggable: false, selectable: false, zIndex: -1
    });

    currentY += laneHeight + 100;
  });

  const finalLaneWidth = GLOBAL_MAX_X + 2500;
  laneNodes.forEach(lane => lane.data.width = finalLaneWidth);

  return { nodes: [...laneNodes, ...questNodes], edges, bounds: { maxX: finalLaneWidth, maxY: currentY } };
};
